// Domain rule: does the persisted betslip belong to the user who is now
// authenticated? Kept as a single pure function (Rules 3, 6, 7) so both the
// hook that applies it and any test exercise the exact same decision. All
// context (persisted owner, current user, whether the slip holds anything) is
// injected — no store or global reads in here (Rule 5).

export type BetslipOwnershipDecision =
  // Belongs to the current user, or there is nothing to protect -> leave as is.
  | { kind: "keep" }
  // Unclaimed slip (fresh / legacy) -> stamp the current user as owner.
  | { kind: "claim" }
  // Held by a DIFFERENT user with selections in it -> wipe before claiming, so
  // one user's slip never leaks to the next on a shared device.
  | { kind: "reset" };

interface BetslipOwnershipInput {
  ownerUserId: number | null;
  currentUserId: number | null;
  hasSelections: boolean;
}

export const decideBetslipOwnership = (
  input: BetslipOwnershipInput
): BetslipOwnershipDecision => {
  const { ownerUserId, currentUserId, hasSelections } = input;

  // Not authenticated yet -> never touch the slip.
  if (currentUserId == null) return { kind: "keep" };

  // Already this user's slip -> nothing to do.
  if (ownerUserId === currentUserId) return { kind: "keep" };

  // A different, known owner still has selections here -> must wipe.
  if (ownerUserId != null && hasSelections) return { kind: "reset" };

  // Unclaimed (owner null) or empty -> safe to claim for the current user.
  return { kind: "claim" };
};
